import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { type RouterOutputs, useTRPC } from "src/api/trpc";

export type Workspace = RouterOutputs["v1"]["workspace"]["get"];
export type WorkspaceLocation = Workspace["locations"][number];
export type WorkspaceConnection = Workspace["model"]["design"]["connections"][number];

/** Everything the workspace, the solution pill and the overview show for one solution. */
export const useWorkspace = (solutionId: string | undefined) => {
	const trpc = useTRPC();
	return useQuery({
		...trpc.v1.workspace.get.queryOptions({ solutionId: solutionId ?? "" }),
		enabled: Boolean(solutionId)
	});
};

/**
 * Workspace edits; each refreshes the workspace, the solution lists (counts, stage, "Updated …") and
 * the projects. Locations and the project context are project data (`projects.byId`), so a stale
 * project would show removed locations or seed its context form with values a later save overwrites.
 */
export const useWorkspaceMutations = () => {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const onSuccess = useCallback(async () => {
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: trpc.v1.workspace.get.pathKey() }),
			queryClient.invalidateQueries({ queryKey: trpc.v1.solutions.pathKey() }),
			queryClient.invalidateQueries({ queryKey: trpc.v1.projects.pathKey() })
		]);
	}, [queryClient, trpc]);

	return {
		addLocation: useMutation(trpc.v1.workspace.addLocation.mutationOptions({ onSuccess })),
		removeLocation: useMutation(trpc.v1.workspace.removeLocation.mutationOptions({ onSuccess })),
		addMetro: useMutation(trpc.v1.workspace.addMetro.mutationOptions({ onSuccess })),
		removeMetro: useMutation(trpc.v1.workspace.removeMetro.mutationOptions({ onSuccess })),
		addCloudRegion: useMutation(trpc.v1.workspace.addCloudRegion.mutationOptions({ onSuccess })),
		removeCloudRegion: useMutation(trpc.v1.workspace.removeCloudRegion.mutationOptions({ onSuccess })),
		addConnection: useMutation(trpc.v1.workspace.addConnection.mutationOptions({ onSuccess })),
		removeConnection: useMutation(trpc.v1.workspace.removeConnection.mutationOptions({ onSuccess })),
		addDeployment: useMutation(trpc.v1.workspace.addDeployment.mutationOptions({ onSuccess })),
		updateContext: useMutation(trpc.v1.projects.updateContext.mutationOptions({ onSuccess }))
	};
};
